import { localeSliceSelectors } from '@/entities/locale/model/locale.selector';
import { env } from '@/env';
import { store } from '@/lib/store';

// --- Configuration ---

const API_BASE_URL = `${env.VITE_API_URL}/api`;

console.log('API_BASE_URL', API_BASE_URL);

// --- Custom Error Type ---

/**
 * A custom error class for API responses.
 * This allows us to throw an error object that contains the status and server message,
 * which is very useful for handling errors in React Query's `useMutation`.
 */
export class ApiError extends Error {
	status: number;
	messages: string[];

	constructor(status: number, messages: string[] = ['An unexpected error occurred.']) {
		super(messages[0] || 'API Error');
		this.name = 'ApiError';
		this.status = status;
		this.messages = messages;
	}
}

// --- Core API Client Function ---

type RequestOptions = Omit<RequestInit, 'body'> & {
	body?: unknown;
	params?: Record<string, string | number | undefined>;
};

/**
 * The core, generic API request function.
 * All other functions (get, post, etc.) are wrappers around this.
 */
async function apiClient<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
	const localeCode = localeSliceSelectors.selectActiveLocale(store.getState())?.code;
	const headers = new Headers(options.headers || {});
	headers.set('Content-Type', 'application/json');
	headers.set('Accept', 'application/json');

	// --- Authentication (Example) ---
	// You would get your JWT from wherever you store it (e.g., a cookie or secure storage)
	// const token = getAuthToken();
	// if (token) {
	//   headers.set('Authorization', `Bearer ${token}`);
	// }

	const config: RequestInit = {
		method: options.method || 'GET',
		headers,
	};

	let fullUrl = `${API_BASE_URL}/${endpoint}`;

	if (config.method === 'GET' && options.params) {
		const params = new URLSearchParams();
		// Add request-specific params
		Object.entries(options.params).forEach(([key, value]) => {
			if (value !== undefined) {
				params.append(key, String(value));
			}
		});
		// ** Automatically inject locales for GET requests **
		if (localeCode) {
			params.append('locale', localeCode);
		}

		fullUrl += `?${params.toString()}`;
	} else if (config.method === 'POST' || config.method === 'PATCH' || config.method === 'PUT') {
		// Always include the locale and fallback locale in the request body for the server to use
		// in case it needs to send localized responses.
		const requestBody = {
			...(options.body || {}),
			context: {
				locale: localeCode,
			},
		};
		config.body = JSON.stringify(requestBody);
	}

	const response = await fetch(fullUrl, config);

	if (!response.ok) {
		let errorMessages = ['Request failed'];
		try {
			const errorData = await response.json();
			// @todo: make sure this is the case: Assumes your Hono error response has
			// a shape like { errors: ["message1", "message2"] }
			errorMessages = errorData.errors || [errorData.message || response.statusText];
		} catch (e) {
			// The response body was not valid JSON
			console.error('Error parsing response body:', e);
		}
		throw new ApiError(response.status, errorMessages);
	}

	// Handle 204 No Content response
	if (response.status === 204) {
		return Promise.resolve(null as T);
	}

	return response.json() as Promise<T>;
}

// --- Public Helper Functions ---

const api = {
	get: <T>(endpoint: string, params?: Record<string, string | number | undefined>): Promise<T> =>
		apiClient<T>(endpoint, { method: 'GET', params }),

	post: <T>(endpoint: string, body?: unknown): Promise<T> =>
		apiClient<T>(endpoint, { method: 'POST', body }),

	patch: <T>(endpoint: string, body?: unknown): Promise<T> =>
		apiClient<T>(endpoint, { method: 'PATCH', body }),

	// Use 'remove' instead of 'delete' to avoid using a reserved keyword
	remove: <T>(endpoint: string, body?: unknown): Promise<T> =>
		apiClient<T>(endpoint, { method: 'DELETE', body }),
};

export { api };
